import { parseToml } from './toml.js';

// === Public holiday warnings ===
//
// "Scheduling on a regional holiday" is a standard cross-timezone failure: the
// clock math is perfect and half the invitees are simply not at work. The
// dataset is loaded lazily so it never delays first paint.

export interface HolidayCountry {
  name: string;
  code: string;
  /** True when only fixed-date national holidays are known for this country */
  partial: boolean;
  /** Map of YYYY-MM-DD to holiday name */
  dates: Map<string, string>;
}

export interface HolidayData {
  lastUpdated: string;
  source: string;
  years: number[];
  /** Countries present in the city list for which no data exists at all */
  missing: string[];
  countries: Map<string, HolidayCountry>;
}

export interface HolidayHit {
  countryName: string;
  holidayName: string;
  date: string;
}

let cache: HolidayData | null = null;
let inflight: Promise<HolidayData | null> | null = null;

/** Load the holiday dataset once. Resolves to null if it cannot be fetched. */
export function loadHolidays(): Promise<HolidayData | null> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch('data/holidays.toml');
      if (!res.ok) return null;
      const doc = parseToml(await res.text());

      const countries = new Map<string, HolidayCountry>();
      const rows = (doc['countries'] as Array<Record<string, unknown>>) ?? [];

      for (const row of rows) {
        const name = row['name'] as string;
        const dates = new Map<string, string>();
        for (const packed of (row['dates'] as string[]) ?? []) {
          const sep = packed.indexOf('|');
          if (sep === -1) continue;
          dates.set(packed.slice(0, sep), packed.slice(sep + 1));
        }
        countries.set(name, {
          name,
          code: (row['code'] as string) ?? '',
          partial: row['partial'] === true,
          dates,
        });
      }

      cache = {
        lastUpdated: (doc['last_updated'] as string) ?? '',
        source: (doc['source'] as string) ?? '',
        years: (doc['years'] as number[]) ?? [],
        missing: (doc['countries_missing'] as string[]) ?? [],
        countries,
      };
      return cache;
    } catch {
      return null;
    }
  })();

  return inflight;
}

/** Format a Date as the YYYY-MM-DD key used by the dataset */
export function dateKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Find holidays landing on a participant's local date.
 *
 * `localDates` must be the local calendar date for each participant, not the
 * UTC date — a meeting at 23:00 UTC falls on the following day in Tokyo, and
 * that is the day whose holidays matter to the person in Tokyo.
 */
export function findHolidays(
  data: HolidayData,
  countryNames: string[],
  localDates: string[]
): (HolidayHit | null)[] {
  return countryNames.map((countryName, i) => {
    const country = data.countries.get(countryName);
    const key = localDates[i];
    if (!country || !key) return null;
    const holidayName = country.dates.get(key);
    return holidayName ? { countryName, holidayName, date: key } : null;
  });
}

/** True when we have no data, or only partial data, for a country */
export function coverageNote(data: HolidayData, countryName: string): 'full' | 'partial' | 'none' {
  const c = data.countries.get(countryName);
  if (!c) return 'none';
  return c.partial ? 'partial' : 'full';
}

/** Whether the requested year falls inside the dataset's range */
export function yearCovered(data: HolidayData, year: number): boolean {
  return data.years.includes(year);
}
